import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BreadcrumbHeading,
  DetailsPanel,
  EditIcon,
  FormatInformation,
  IconButton,
  Modal,
  SaladIcon,
  Spinner,
  Table,
} from '@weareopenr/component-library';
import { groupAndFormatSchedules } from '../Mealtimes/groupAndFormatSchedules/groupAndFormatSchedules';
import styles from './Mealtime.module.scss';
import { useMealtimes } from 'src/hooks/useMealtimes/useMealtimes';
import ModalImg from '../../assets/images/section-modal.png';
import MealtimeView from '../../components/MealtimeDetailsModal/MealtimeView/MealtimeView';
import { useCategories } from 'src/hooks/useCategories/useCategories';
import AssignCategoriesView from 'src/components/MealtimeDetailsModal/AssignCategoriesView/AssignCategoriesView';
import EmptyState from 'src/components/EmptyState';
import { getCategoryItemCount } from '../../helpers/getCategoryItemCount/getCategoryItemCount';
import { Routes } from 'src/routes';
import { createColumnHelper } from '@tanstack/react-table';
import { ICategory } from 'src/types/mealtimes.types';

const columnHelper = createColumnHelper<ICategory>();

export const Mealtime = () => {
  const { menuId, id: mealtimeId } = useParams<{ menuId: string; id: string }>();
  const { isLoading, data: mealtimes } = useMealtimes(menuId);

  const mealtime = mealtimes?.find((mealtime) => mealtime.mealtime_id === mealtimeId);

  const [isEditDetailsModalOpen, setIsEditDetailsModalOpen] = useState<boolean>(false);

  const { data, isLoading: isCategoriesLoading } = useCategories(menuId);
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
      }),
      columnHelper.display({
        id: 'number-of-items',
        header: 'Number of items',
        size: 160,
        cell: (info) => {
          const detailedCategory = data?.find((c) => c.section_id === info.row.original.category_id);
          return detailedCategory ? getCategoryItemCount(detailedCategory, data).toString() : '-';
        },
      }),
    ],
    [data],
  );

  const [isEditCategoriesModalOpen, setIsEditCategoriesModalOpen] = useState<boolean>(false);

  const getSchedules = () => {
    const schedules = groupAndFormatSchedules(mealtime.schedules);
    return schedules.map((schedule) => (
      <span className={styles.information} key={schedule.startDay + schedule.endDay + schedule.time}>
        {`${schedule.startDay}${schedule.endDay ? ' - ' + schedule.endDay : ''}: ${schedule.time}`}
      </span>
    ));
  };

  const noCategoryText =
    "Before you can use mealtimes, you'll need to assign at least one menu category. When you create a category, you can assign mealtimes to it directly. Alternatively, you can edit the categories here.";

  const closeModal = () => {
    setIsEditDetailsModalOpen(false);
  };
  return (
    <>
      {isLoading ? (
        <div className={styles['spinner-container']} data-testid="mealtime-loading">
          <Spinner size="xlarge" colour="rainbow" />
        </div>
      ) : (
        <>
          <Modal open={isEditDetailsModalOpen} closeModal={closeModal} image={ModalImg}>
            <MealtimeView
              onSuccess={() => closeModal()}
              menuId={menuId}
              mealtimes={mealtimes.filter((m) => m.mealtime_id !== mealtime.mealtime_id)}
              onModalClose={closeModal}
              type="edit"
              mealtime={mealtime}
            />
          </Modal>
          <Modal
            open={isEditCategoriesModalOpen}
            closeModal={() => setIsEditCategoriesModalOpen(false)}
            image={ModalImg}
          >
            {isCategoriesLoading ? (
              <div className={styles.spinner} data-testid="mealtime-loading">
                <Spinner size="xlarge" colour="rainbow" />
              </div>
            ) : (
              <AssignCategoriesView
                categoriesData={data}
                closeModal={() => setIsEditCategoriesModalOpen(false)}
                mealtimeId={mealtimeId}
                menuId={menuId}
                preselectedCategories={mealtime.categories}
              />
            )}
          </Modal>
          <BreadcrumbHeading
            breadcrumbs={[
              { label: 'Menus', path: Routes.MENUS },
              { label: '...', path: `${Routes.MENUS}/${menuId}` },
              { label: 'Mealtimes', path: `${Routes.MENUS}/${menuId}/mealtimes` },
              { label: mealtime?.name, path: '' },
            ]}
          />
          <div className={styles['container']}>
            <DetailsPanel
              title="Details"
              action={
                <IconButton
                  icon={EditIcon}
                  onClick={() => setIsEditDetailsModalOpen(true)}
                  buttonStyle="ghost"
                  aria-label="Edit details"
                />
              }
            >
              <FormatInformation heading="Name">{mealtime?.name}</FormatInformation>
              <FormatInformation heading="Time availability">{mealtime && getSchedules()}</FormatInformation>
            </DetailsPanel>
            <DetailsPanel
              title="Menu Categories"
              action={
                <IconButton
                  icon={EditIcon}
                  onClick={() => setIsEditCategoriesModalOpen(true)}
                  buttonStyle="ghost"
                  aria-label="Edit categories"
                />
              }
            >
              {isCategoriesLoading || mealtime?.categories.length === 0 ? (
                <EmptyState
                  Icon={() => <SaladIcon size="large" />}
                  emptySectionText={noCategoryText}
                  buttonText="Edit"
                  onClick={() => setIsEditCategoriesModalOpen(true)}
                />
              ) : (
                <div className={styles['table-container']}>
                  <Table columns={columns} data={mealtime.categories} />
                </div>
              )}
            </DetailsPanel>
          </div>
        </>
      )}
    </>
  );
};
